import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./StarRewardBadge.css";

// The celebratory "+N stars earned!" moment shown on a game's result overlay
// when a daily star reward lands — a faithful port of iOS `StarRewardBadge`:
// a burst of gold stars radiates out, a glow flashes, the badge springs in with
// a shimmer sweep, and the number counts up.

const PARTICLES = Array.from({ length: 12 }, (_, index) => {
  const angle = (index / 12) * Math.PI * 2;
  const distance = 46 + (index % 4) * 12; // 46…82
  return {
    id: index,
    dx: Math.cos(angle) * distance,
    dy: Math.sin(angle) * distance,
    size: 10 + (index % 3) * 4,
    rotate: -60 + (index % 5) * 30,
  };
});

const StarRewardBadge = ({ count = 0 }) => {
  const [shown, setShown] = useState(0);

  // Count the number up from zero once the badge has landed.
  useEffect(() => {
    const total = Math.max(count, 1);
    let current = 0;
    const step = Math.max(30, Math.min(60, 600 / total));
    const id = window.setInterval(() => {
      current += 1;
      setShown(current);
      if (current >= total) window.clearInterval(id);
    }, step);
    return () => window.clearInterval(id);
  }, [count]);

  return (
    <div className="srb-wrap" aria-label={`${count} stars earned`}>
      {/* Soft glow flash behind the badge. */}
      <motion.span
        className="srb-glow"
        aria-hidden="true"
        initial={{ scale: 0.65, opacity: 0.9 }}
        animate={{ scale: 1.15, opacity: 0 }}
        transition={{ duration: 0.85, ease: "easeOut" }}
      />

      {/* Burst of stars flying outward + fading. */}
      {PARTICLES.map((p) => (
        <motion.span
          key={p.id}
          className="srb-particle"
          aria-hidden="true"
          style={{ fontSize: p.size }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 }}
          animate={{ x: p.dx, y: p.dy, opacity: 0, scale: 0.3, rotate: p.rotate }}
          transition={{ duration: 0.85, ease: "easeOut" }}
        >
          ★
        </motion.span>
      ))}

      {/* The badge itself. */}
      <motion.div
        className="srb-badge"
        initial={{ scale: 0.3, rotate: -6, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 14 }}
      >
        <motion.span
          className="srb-star"
          aria-hidden="true"
          initial={{ scale: 0.6 }}
          animate={{ scale: [0.6, 1.25, 1] }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          ★
        </motion.span>
        <span className="srb-text">+{shown} stars!</span>
        <span className="srb-shimmer" aria-hidden="true" />
      </motion.div>
    </div>
  );
};

export default StarRewardBadge;
